import type { HTMLAttributes, ReactNode, Ref } from 'react';
import { useI18nT } from '../../i18n/index.js';

export type ProgressTone = 'accent' | 'success' | 'danger';
export type ProgressVariant = 'linear' | 'circular';

export interface ProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
  /** 0–100 for determinate; omit for indeterminate. */
  value?: number;
  tone?: ProgressTone;
  variant?: ProgressVariant;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Only for circular: stroke thickness in px. */
  thickness?: number;
  className?: string;
  ref?: Ref<HTMLDivElement>;
}

const CIRCULAR_SIZE: Record<'sm' | 'md' | 'lg', number> = {
  sm: 24,
  md: 36,
  lg: 56,
};

/** Progress indicator — linear bar or circular ring. Omit `value` for
 *  indeterminate animation. Ring math: circumference = 2πr; stroke-dashoffset
 *  of `circumference * (1 - pct/100)` reveals the filled arc. */
export function Progress(props: ProgressProps): ReactNode {
  const {
    value,
    tone = 'accent',
    variant = 'linear',
    label,
    size = 'md',
    thickness,
    className,
    ref,
    ...rest
  } = props;

  const t = useI18nT();
  const indeterminate = value === undefined;
  const pct = indeterminate ? 0 : Math.min(100, Math.max(0, value));
  const classes = ['vq-progress', className].filter(Boolean).join(' ');
  const announced = label ?? t('progress.defaultLabel');

  if (variant === 'circular') {
    const px = CIRCULAR_SIZE[size];
    const stroke = thickness ?? Math.max(2, Math.round(px / 10));
    const r = (px - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = indeterminate ? c * 0.75 : c * (1 - pct / 100);
    return (
      <div
        {...rest}
        ref={ref}
        className={classes}
        data-component="Progress"
        data-tone={tone}
        data-size={size}
        data-variant="circular"
        data-indeterminate={indeterminate ? 'true' : 'false'}
        role="progressbar"
        aria-label={announced}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : pct}
      >
        <svg
          className="vq-progress__ring"
          viewBox={`0 0 ${String(px)} ${String(px)}`}
          width={px}
          height={px}
          aria-hidden="true"
        >
          <circle
            className="vq-progress__ring-track"
            cx={px / 2}
            cy={px / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
          />
          <circle
            className="vq-progress__ring-fill"
            cx={px / 2}
            cy={px / 2}
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${String(px / 2)} ${String(px / 2)})`}
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      {...rest}
      ref={ref}
      className={classes}
      data-component="Progress"
      data-tone={tone}
      data-size={size}
      data-variant="linear"
      data-indeterminate={indeterminate ? 'true' : 'false'}
      role="progressbar"
      aria-label={announced}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : pct}
    >
      <div
        className="vq-progress__bar"
        style={indeterminate ? undefined : { width: `${pct.toFixed(2)}%` }}
      />
    </div>
  );
}
